import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { getLocaleSelected } from '@rd/core/locale/store/selectors/locale.selectors';
import { Locale } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { filter, Subscription } from 'rxjs';
import { CoreState } from '../../store/reducers';
import {
  getTenantBootstrapTimeZoneOffset,
  getTenantBootstrapTimeZoneOffsetInMinutes,
} from '../../tenant/store/selectors/tenant.selectors';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

type TimezoneFormat = 'noTimezone' | string | undefined;

@Injectable({
  providedIn: 'root',
})
export class DateService {
  currentLocale$: Subscription;
  currentLocale: Locale | null;
  timeZoneOffsetInMinutes = 0;
  timeZoneOffset = '+00:00';

  ISO8601_DATE_REGEX =
    '/^(d{4})-?(dd)-?(dd)(?:T(dd)(?::?(dd)(?::?(dd)(?:.(d+))?)?)?(Z|([+-])(dd):?(dd))?)?$/';

  dayjs = dayjs;

  constructor(
    private store: Store<CoreState>,
    private sentryService: SentryService,
  ) {
    this.currentLocale$ = this.store
      .select(getLocaleSelected)
      .pipe(filter(isNotNull))
      .subscribe((locale: Locale) => {
        this.currentLocale = locale;
        this.dayjs.locale(DayJsConfig.localeCodeMapper(locale.code));
      });

    this.store
      .select(getTenantBootstrapTimeZoneOffsetInMinutes)
      .subscribe((timeZoneOffsetInMinutes: number) => {
        this.timeZoneOffsetInMinutes = timeZoneOffsetInMinutes;
      });

    this.store
      .select(getTenantBootstrapTimeZoneOffset)
      .subscribe((timeZoneOffset: string | undefined) => {
        if (timeZoneOffset) {
          this.timeZoneOffset = timeZoneOffset;
        } else {
          this.sentryService.manualError(
            'Bootstrap Timezone Offset is undefined.',
          );
        }
      });
  }

  formatDate(
    value: string | number | Date,
    format: string,
    timezone?: TimezoneFormat,
    localeCode?: string,
  ): string {
    const parsedValue = this.parseDate(value, timezone);
    // formatDate function throw error if localeCode is missing
    if (this.currentLocale?.code || localeCode) {
      return formatDate(
        parsedValue,
        format,
        // @ts-ignore string | undefined is not assignable to string
        this.currentLocale?.code || localeCode,
        timezone || this.timeZoneOffset,
      );
    }
    return '';
  }

  recentMonthNames(duration: number = 6): string[] {
    const indices = Array(duration)
      .fill(0)
      .map((_, index) => duration - index - 1);

    return indices.map((offset) => {
      const monthDate = this.dayjs().subtract(offset, 'month').format();

      return this.formatDate(monthDate, 'MMM');
    });
  }

  parseDate(value: string | number | Date, timezone?: TimezoneFormat): Date {
    if (this.isDate(value)) {
      return value;
    }

    if (typeof value === 'number' && !isNaN(value)) {
      return new Date(value);
    }

    if (typeof value === 'string') {
      value = value.trim();

      const parsedNb = parseFloat(value);

      // any string that only contains numbers, like "1234" but not like "1234hello"
      if (!isNaN((value as any) - parsedNb)) {
        return new Date(parsedNb);
      }

      if (/^(\d{4}-\d{1,2}-\d{1,2})$/.test(value)) {
        /* For ISO Strings without time the day, month and year must be extracted from the ISO String
        before Date creation to avoid time offset and errors in the new Date.
        If we only replace '-' with ',' in the ISO String ("2015,01,01"), and try to create a new
        date, some browsers (e.g. IE 9) will throw an invalid Date error.
        If we leave the '-' ("2015-01-01") and try to create a new Date("2015-01-01") the timeoffset
        is applied.
        Note: ISO months are 0 for January, 1 for February, ... */
        const [y, m, d] = value.split('-').map((val: string) => +val);
        return new Date(y, m - 1, d);
      }

      // NN's date format is dd-mm-yyyy on some locales.
      if (/^(\d{1,2}-\d{1,2}-\d{4})$/.test(value)) {
        const [d, m, y] = value.split('-').map((val: string) => +val);
        return new Date(y, m - 1, d);
      }

      let match: RegExpMatchArray | null;
      // eslint-disable-next-line no-cond-assign
      if ((match = value.match(this.ISO8601_DATE_REGEX))) {
        return this.isoStringToDate(match);
      }
    }

    let validDateContent = value;
    if (timezone === 'noTimezone' && typeof value === 'string') {
      validDateContent = value.split('+')[0].trim();
    }

    const date = new Date(validDateContent as any);
    if (!this.isDate(date)) {
      throw new Error(`Unable to convert "${value}" into a date`);
    }
    return date;
  }

  isDate(value: any): value is Date {
    return value instanceof Date && !isNaN(value.valueOf());
  }

  private isoStringToDate(match: RegExpMatchArray): Date {
    const date = new Date(0);
    let tzHour = 0;
    let tzMin = 0;

    // match[8] means that the string contains "Z" (UTC) or a timezone like "+01:00" or "+0100"
    const dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear;
    const timeSetter = match[8] ? date.setUTCHours : date.setHours;

    // if there is a timezone defined like "+01:00" or "+0100"
    if (match[9]) {
      tzHour = Number(match[9] + match[10]);
      tzMin = Number(match[9] + match[11]);
    }
    dateSetter.call(
      date,
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    const h = Number(match[4] || 0) - tzHour;
    const m = Number(match[5] || 0) - tzMin;
    const s = Number(match[6] || 0);
    const ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
    timeSetter.call(date, h, m, s, ms);
    return date;
  }

  lastDayOfMonth(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 0);
  }

  lastDayOfYear(year: number): Date {
    return new Date(year, 11, 31);
  }

  lastDayOfCurrentLocalYear(): Date {
    const currentYear = dayjs().utc().year();

    return this.lastDayOfYear(currentYear);
  }

  /**
   * Helper function to ensure that the Date object is constructed relative to the
   * tenant's timezone (NZDT for BNZ) rather than the user's local browser timezone.
   *
   * WHY WE NEED THIS:
   * The application is used by users who may be in different timezones,
   * but the banking operations (like Auto Redemption) must align with the bank's local date (NZDT).
   * Without this conversion, a user in a timezone behind NZ (like SGT, PST, etc.) might select
   * "Today" or "Tomorrow" based on their local time, which could be a different date in NZ.
   *
   * WHAT IT DOES:
   * 1. Takes a source date (typically from the browser's local timezone).
   * 2. Uses `dateService.timeZoneOffsetInMinutes` (e.g., NZDT offset) to shift the moment to the target timezone.
   * 3. Extracts the Year, Month, and Day from that shifted moment.
   * 4. Constructs a new Date object using these components. This results in a Date object that,
   *    when printed or used locally, represents the Year/Month/Day of the target timezone.
   *
   * TECHNICAL NOTE:
   * Simply using `dayjs(date).utcOffset(offset).toDate()` does NOT work because `.toDate()` converts
   * the DayJS object back to a standard JavaScript Date in the *browser's local timezone*, effectively
   * discarding the offset shift for the purpose of the date components (Year/Month/Day).
   * While `dayjs().format()` respects the offset, Pikaday (the date picker library) requires a genuine
   * JavaScript `Date` object, not a string. Therefore, we manually reconstruct the Date object using
   * the shifted Year, Month, and Day to "trick" the Date object into representing the target timezone's
   * date as if it were local.
   *
   * EXAMPLES (Assuming NZDT is UTC+13):
   *
   * 1. SGT (UTC+8) -> NZDT (UTC+13) [5 hours ahead]
   *    - Case A: User is in Singapore at 6:00 PM on Dec 8th.
   *      NZ time is 11:00 PM on Dec 8th.
   *      Result: Date object representing Dec 8th.
   *    - Case B: User is in Singapore at 8:00 PM on Dec 8th.
   *      NZ time is 1:00 AM on Dec 9th.
   *      Result: Date object representing Dec 9th.
   *
   * 2. San Francisco (PST, UTC-8) -> NZDT [21 hours ahead]
   *    - Case A: User is in SF at 1:00 AM on Dec 8th.
   *      NZ time is 10:00 PM on Dec 8th.
   *      Result: Date object representing Dec 8th.
   *    - Case B: User is in SF at 4:00 PM on Dec 8th.
   *      NZ time is 1:00 PM on Dec 9th.
   *      Result: Date object representing Dec 9th.
   *
   * 3. Hawaii (HST, UTC-10) -> NZDT [23 hours ahead]
   *    - Case A: User is in Hawaii at 12:30 AM on Dec 8th.
   *      NZ time is 11:30 PM on Dec 8th.
   *      Result: Date object representing Dec 8th.
   *    - Case B: User is in Hawaii at 2:00 PM on Dec 8th.
   *      NZ time is 1:00 PM on Dec 9th.
   *      Result: Date object representing Dec 9th.
   *
   * 4. London (GMT, UTC+0) -> NZDT [13 hours ahead]
   *    - Case A: User is in London at 10:00 AM on Dec 8th.
   *      NZ time is 11:00 PM on Dec 8th.
   *      Result: Date object representing Dec 8th.
   *    - Case B: User is in London at 12:00 PM (Noon) on Dec 8th.
   *      NZ time is 1:00 AM on Dec 9th.
   *      Result: Date object representing Dec 9th.
   */
  toUTCDateWithOffset(dateValue: Date): Date {
    const dayjsOffset = this.dayjs(dateValue).utcOffset(
      this.timeZoneOffsetInMinutes,
    );

    return new Date(
      dayjsOffset.year(),
      dayjsOffset.month(),
      dayjsOffset.date(),
    );
  }

  isDateTodayInUserTimezone(date: Date | string | undefined): boolean {
    const currentLocale = this.currentLocale?.code;

    if (!date || !currentLocale) {
      return false;
    }

    // Since every date in NN is UTC, we need to convert it to tenant's timezone.
    const today = new Date();
    const format = 'dd MM';

    return (
      formatDate(date, format, currentLocale) ===
      formatDate(today, format, currentLocale)
    );
  }

  isSameDate(date1: Date | null, date2: Date | null): boolean {
    if (!date1 || !date2) return false;

    return this.dayjs(date1).isSame(this.dayjs(date2));
  }
}
